import { Controller } from "@hotwired/stimulus"
import calendarjs from "@calendarjs/ce"

const { Calendar } = calendarjs

export default class extends Controller {
  static targets = [
    "calendar",
    "modal",
    "backdrop",
    "selectedDateLabel",
    "statusSummary",
    "associatedOrders",
    "dateField",
    "monthField",
    "maxOrdersField",
    "activeField"
  ]

  static values = {
    currentMonth: String,
    windows: Object,
    orderCounts: Object,
    ordersByDate: Object
  }

  connect() {
    this.visibleDate = this.currentMonthValue
    this.visibleMonth = this.monthKey(this.currentMonthValue)
    this.renderCalendar()
    this.boundCalendarClick = this.handleCalendarClick.bind(this)
    this.calendarTarget.addEventListener("click", this.boundCalendarClick)
  }

  disconnect() {
    if (this.calendar?.destroy) {
      this.calendar.destroy()
    }

    if (this.boundCalendarClick) {
      this.calendarTarget.removeEventListener("click", this.boundCalendarClick)
    }
  }

  close() {
    this.modalTarget.classList.add("hidden")
    this.backdropTarget.classList.add("hidden")
  }

  renderCalendar() {
    this.calendar = Calendar(this.calendarTarget, {
      type: "inline",
      value: this.currentMonthValue,
      format: "yyyy-mm-dd",
      footer: false,
      wheel: false,
      startingDay: 0,
      onupdate: (_self, value) => {
        const normalized = this.normalizeDate(value)
        if (normalized) {
          this.visibleDate = normalized
        }
        this.decorateCalendarDays()
      },
      onchange: (_self, value) => {
        this.openDate(this.normalizeDate(value))
      }
    })

    this.decorateCalendarDays()
  }

  handleCalendarClick(event) {
    const navigationButton = event.target.closest(".lm-calendar-navigation button")
    const currentView = this.calendarTarget.querySelector(".lm-calendar-container")?.dataset.view
    const viewOption = event.target.closest(".lm-calendar-content > div")

    if (!navigationButton && !(viewOption && currentView && currentView !== "days")) {
      return
    }

    requestAnimationFrame(() => {
      this.syncVisibleMonthFromCalendar()
    })
  }

  syncVisibleMonthFromCalendar() {
    const nextVisibleMonth = this.visibleMonthKeyFromCalendar()
    if (!nextVisibleMonth || nextVisibleMonth === this.visibleMonth) {
      return
    }

    this.navigateToMonth(nextVisibleMonth)
  }

  decorateCalendarDays() {
    const dayCells = this.calendarTarget.querySelectorAll(".lm-calendar-content > div")
    if (!dayCells.length) return

    const viewDate = this.visibleDate ? new Date(`${this.visibleDate}T00:00:00`) : new Date()
    const firstDayOfMonth = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1)
    const firstCalendarDate = new Date(firstDayOfMonth)
    firstCalendarDate.setDate(firstCalendarDate.getDate() - firstCalendarDate.getDay())

    dayCells.forEach((cell, index) => {
      const mappedDate = new Date(firstCalendarDate)
      mappedDate.setDate(firstCalendarDate.getDate() + index)
      const isoDate = this.toIsoDate(mappedDate)
      const isCurrentMonth = mappedDate.getMonth() === viewDate.getMonth()

      cell.dataset.isoDate = isoDate
      cell.classList.toggle("is-outside-month", !isCurrentMonth)

      const oldBadges = cell.querySelector(".delivery-day-badges")
      if (oldBadges) oldBadges.remove()

      const oldOrders = cell.querySelector(".delivery-day-orders")
      if (oldOrders) oldOrders.remove()

      const dayConfig = this.windowsValue[isoDate] || { active: false, max_orders: 0 }
      const maxOrders = Number(dayConfig.max_orders || 0)
      const usedOrders = Number(this.orderCountsValue[isoDate] || 0)
      const remainingOrders = Math.max(maxOrders - usedOrders, 0)
      const orders = this.ordersByDateValue[isoDate] || []

      const badges = document.createElement("div")
      badges.className = "delivery-day-badges"

      const totalBadge = document.createElement("span")
      totalBadge.className = "delivery-day-status"
      totalBadge.dataset.state = dayConfig.active ? "total" : "closed"
      totalBadge.textContent = dayConfig.active ? `Total: ${maxOrders}` : "Fechado"
      badges.appendChild(totalBadge)

      const remainingBadge = document.createElement("span")
      remainingBadge.className = "delivery-day-status"
      if (!dayConfig.active) {
        remainingBadge.dataset.state = "closed"
        remainingBadge.textContent = "Restam: 0"
      } else if (remainingOrders <= 0) {
        remainingBadge.dataset.state = "full"
        remainingBadge.textContent = "Restam: 0"
      } else {
        remainingBadge.dataset.state = "open"
        remainingBadge.textContent = `Restam: ${remainingOrders}`
      }
      badges.appendChild(remainingBadge)

      cell.appendChild(badges)

      if (orders.length > 0) {
        const ordersList = document.createElement("div")
        ordersList.className = "delivery-day-orders"

        orders.slice(0, 3).forEach((order) => {
          const orderItem = document.createElement("div")
          orderItem.className = "delivery-day-order"
          orderItem.dataset.status = order.status
          orderItem.innerHTML = `<strong>${order.code}</strong><span>${order.customer_name}</span>`
          ordersList.appendChild(orderItem)
        })

        if (orders.length > 3) {
          const more = document.createElement("div")
          more.className = "delivery-day-order"
          more.textContent = `+ ${orders.length - 3} pedidos`
          ordersList.appendChild(more)
        }

        cell.appendChild(ordersList)
      }
    })
  }

  openDate(isoDate) {
    if (!isoDate) return

    const dayConfig = this.windowsValue[isoDate] || { active: false, max_orders: 0 }
    const maxOrders = Number(dayConfig.max_orders || 0)
    const usedOrders = Number(this.orderCountsValue[isoDate] || 0)
    const remainingOrders = Math.max(maxOrders - usedOrders, 0)
    const orders = this.ordersByDateValue[isoDate] || []

    this.dateFieldTarget.value = isoDate
    this.monthFieldTarget.value = isoDate.slice(0, 7)
    this.maxOrdersFieldTarget.value = maxOrders
    this.activeFieldTarget.checked = Boolean(dayConfig.active)
    this.selectedDateLabelTarget.textContent = this.toHumanDate(isoDate)
    this.statusSummaryTarget.textContent = `Pedidos: ${usedOrders} / ${maxOrders} (livres: ${remainingOrders})`
    this.renderAssociatedOrders(orders)

    this.backdropTarget.classList.remove("hidden")
    this.modalTarget.classList.remove("hidden")
  }

  renderAssociatedOrders(orders) {
    this.associatedOrdersTarget.innerHTML = ""

    if (orders.length === 0) {
      this.associatedOrdersTarget.innerHTML = '<p class="delivery-modal-empty-orders">Nenhum pedido associado a esta data.</p>'
      return
    }

    const list = document.createElement("div")
    list.className = "delivery-modal-orders-list"

    orders.forEach((order) => {
      const item = document.createElement("a")
      item.className = "delivery-modal-order-link"
      item.href = order.url
      item.innerHTML = `<strong>${order.code}</strong><span>${order.customer_name}</span><small>${order.status_label}</small>`
      list.appendChild(item)
    })

    this.associatedOrdersTarget.appendChild(list)
  }

  normalizeDate(value) {
    if (!value) return ""

    if (typeof value === "string") {
      return value.slice(0, 10)
    }

    if (typeof value === "number") {
      return this.excelSerialToIso(value)
    }

    return ""
  }

  excelSerialToIso(serial) {
    const base = new Date(Date.UTC(1899, 11, 30))
    const date = new Date(base.getTime() + Math.floor(serial) * 86400000)
    const year = date.getUTCFullYear()
    const month = String(date.getUTCMonth() + 1).padStart(2, "0")
    const day = String(date.getUTCDate()).padStart(2, "0")
    return `${year}-${month}-${day}`
  }

  toHumanDate(isoDate) {
    const [year, month, day] = isoDate.split("-")
    return `${day}/${month}/${year}`
  }

  toIsoDate(date) {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")
    return `${year}-${month}-${day}`
  }

  monthKey(isoDate) {
    return isoDate.slice(0, 7)
  }

  visibleMonthKeyFromCalendar() {
    const year = String(this.calendar?.year || "")
    const monthName = String(this.calendar?.month || "").trim().toLowerCase()
    if (!year || !monthName) {
      return null
    }

    const monthIndex = this.monthNameMap()[monthName]
    if (monthIndex == null) {
      return null
    }

    return `${year}-${String(monthIndex + 1).padStart(2, "0")}`
  }

  monthNameMap() {
    if (this._monthNameMap) {
      return this._monthNameMap
    }

    const locales = [document.documentElement.lang || navigator.language, "pt-BR", "en-US"].filter(Boolean)
    const map = {}

    locales.forEach((locale) => {
      for (let index = 0; index < 12; index += 1) {
        const date = new Date(Date.UTC(2026, index, 1))
        const longName = new Intl.DateTimeFormat(locale, { month: "long", timeZone: "UTC" }).format(date).toLowerCase()
        const shortName = new Intl.DateTimeFormat(locale, { month: "short", timeZone: "UTC" }).format(date).replace(".", "").toLowerCase()
        map[longName] = index
        map[shortName] = index
      }
    })

    this._monthNameMap = map
    return map
  }

  navigateToMonth(month) {
    const url = new URL(window.location.href)
    url.searchParams.set("month", month)

    if (window.Turbo?.visit) {
      window.Turbo.visit(url.toString())
      return
    }

    window.location.assign(url.toString())
  }
}
